import { FreeRoundType, localStorageHelper } from 'utils';
import { IPromotionData } from '../../helpers/interfaces';

export const OPERATOR_CAMPAIGN_OWNER = 'OPERATOR';
export const CAPTAIN_UP_CAMPAIGN_OWNER = 'CAPTAINUP';
export const MANAGE_CAMPAIGNS_AUTHORITY = 'MANAGE_CAMPAIGNS';
export const MANAGE_SUPPLIERS_CAMPAIGNS_AUTHORITY = 'MANAGE_SUPPLIERS_CAMPAIGNS';

const getUserAuthorities = (): string[] => localStorageHelper.getChainedValue('user.authorities') || [];
const getBrandFeatures = () => localStorageHelper.getChainedValue('user.brandFeatures') || {};

export const hasManageCampaignsAuthority = () =>
    getUserAuthorities().includes(MANAGE_CAMPAIGNS_AUTHORITY);

export const hasManageSuppliersCampaignsAuthority = () =>
    getUserAuthorities().includes(MANAGE_SUPPLIERS_CAMPAIGNS_AUTHORITY);

export const supportsThirdPartyCampaigns = () => Boolean(getBrandFeatures().supportsGameEvents);

export const canViewThirdPartyCampaignFeatures = () => supportsThirdPartyCampaigns();

export const canCreatePromotionCampaign = () =>
    hasManageCampaignsAuthority() || hasManageSuppliersCampaignsAuthority();

export const getCampaignOwner = (campaign?: Pick<IPromotionData, 'owner'> | null) =>
    campaign?.owner || OPERATOR_CAMPAIGN_OWNER;

export const isOperatorCampaign = (campaign?: Pick<IPromotionData, 'owner'> | null) =>
    getCampaignOwner(campaign) === OPERATOR_CAMPAIGN_OWNER;

export const isThirdPartyCampaign = (campaign?: Pick<IPromotionData, 'owner'> | null) =>
    !isOperatorCampaign(campaign);

export const canManagePromotionCampaign = (campaign?: Pick<IPromotionData, 'owner'> | null) =>
    isThirdPartyCampaign(campaign)
        ? hasManageSuppliersCampaignsAuthority()
        : hasManageCampaignsAuthority();

export const getCampaignOwnershipLabel = (campaign?: Pick<IPromotionData, 'owner'> | null) =>
    isThirdPartyCampaign(campaign) ? 'Third Party' : 'Operator';

export const getCampaignOwnershipTagColor = (campaign?: Pick<IPromotionData, 'owner'> | null) =>
    isThirdPartyCampaign(campaign) ? 'purple' : 'blue';

export const getCampaignOwnerDisplayName = (
    campaign?: Pick<IPromotionData, 'owner'> | null,
) => {
    const owner = getCampaignOwner(campaign);

    if (owner === CAPTAIN_UP_CAMPAIGN_OWNER) {
        return 'External';
    }

    return 'Internal';
};

export const THIRD_PARTY_CAMPAIGN_OWNERS = [
    {
        label: 'External',
        value: CAPTAIN_UP_CAMPAIGN_OWNER,
    },
];

export const getPromotionCampaignOwnerFilterOptions = () => [
    {
        label: 'My Campaigns',
        value: OPERATOR_CAMPAIGN_OWNER,
    },
    {
        label: 'External campaigns',
        value: CAPTAIN_UP_CAMPAIGN_OWNER,
    },
];

export const getCurrencyFractionDigits = (subunit?: number | null) => {
    if (!subunit || subunit <= 1) {
        return 0;
    }

    const digits = Math.log10(subunit);

    return Number.isInteger(digits) ? digits : 2;
};

export const formatPromotionTotalCost = (value: number, subunit?: number | null) => {
    const safeValue = Number.isFinite(value) ? value : 0;
    const fractionDigits = getCurrencyFractionDigits(subunit);

    return new Intl.NumberFormat(undefined, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    }).format(safeValue);
};

export const getTerminateCampaignPopupText = (t: (key: string) => string) => {
    const freeRoundsType = localStorageHelper.getChainedValue('user.brandFeatures.freeRoundsType');

    return freeRoundsType === FreeRoundType.SECONDARY_FLOW
        ? t('after_terminating_the_campaign_secondary_flow')
        : t('after_terminating_the_campaign_primary_flow');
};
